/**
 * ET4: etiketternas utskrift i apparna som en färdig PDF med fasta mått, så att inget i webbvyns
 * bredd, zoom eller print-CSS kan ändra arket. Här finns den rena delen (var etiketterna hamnar och
 * PDF-filen); ritningen av varje sida ligger i labelCanvas.ts.
 */
import type { SheetFormat } from './labelLayout';

/** A4 med 5 mm marginal uppe/nere och 7 mm på sidorna → 196 × 287 mm etikettyta (som @page). */
export const A4 = { wMm: 210, hMm: 297 };
export const SHEET_MARGIN_MM = { top: 5, side: 7 };
export const SHEET_AREA_MM = { w: A4.wMm - 2 * SHEET_MARGIN_MM.side, h: A4.hMm - 2 * SHEET_MARGIN_MM.top };
export const LABEL_H_MM = 55;
const GAP_MM = 2;

export interface LabelRect { x: number; y: number; w: number; h: number }
export interface PdfPageLayout<T> { wMm: number; hMm: number; cells: Array<LabelRect & { item: T }> }

/**
 * Etiketternas rutor på A4-arken, samma som rutnätet i LabelPrinter (renderSheet): kolumner med
 * 2 mm mellanrum; 55 mm höga rader jämnt fördelade om de får plats, annars lika höga med 2 mm mellanrum.
 */
export function sheetCellRects(sheet: SheetFormat): LabelRect[] {
  const { cols, rows } = sheet;
  const w = (SHEET_AREA_MM.w - (cols - 1) * GAP_MM) / cols;
  const fixed = rows * LABEL_H_MM <= SHEET_AREA_MM.h;
  const h = fixed ? LABEL_H_MM : (SHEET_AREA_MM.h - (rows - 1) * GAP_MM) / rows;
  const rowStep = rows > 1 ? h + (SHEET_AREA_MM.h - rows * h) / (rows - 1) : 0;
  const rects: LabelRect[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      rects.push({ x: SHEET_MARGIN_MM.side + c * (w + GAP_MM), y: SHEET_MARGIN_MM.top + r * rowStep, w, h });
    }
  }
  return rects;
}

/** Ark: en A4-sida per rows × cols etiketter. */
export function sheetPdfPages<T>(items: T[], sheet: SheetFormat): PdfPageLayout<T>[] {
  const rects = sheetCellRects(sheet);
  const pages: PdfPageLayout<T>[] = [];
  for (let i = 0; i < items.length; i += rects.length) {
    pages.push({ wMm: A4.wMm, hMm: A4.hMm, cells: items.slice(i, i + rects.length).map((item, k) => ({ ...rects[k], item })) });
  }
  return pages;
}

/** Etikettskrivare: en sida per etikett i etikettens mått. */
export function printerPdfPages<T>(items: T[], size: { wMm: number; hMm: number }): PdfPageLayout<T>[] {
  return items.map(item => ({ wMm: size.wMm, hMm: size.hMm, cells: [{ x: 0, y: 0, w: size.wMm, h: size.hMm, item }] }));
}

export const PT_PER_MM = 72 / 25.4;

export interface PdfImagePage {
  wMm: number;
  hMm: number;
  /** Hela sidan som JPEG. */
  jpeg: Uint8Array;
  pxW: number;
  pxH: number;
}

/** En PDF där varje sida är en JPEG-bild över hela sidan (inget beroende behövs). */
export function buildImagePdf(pages: PdfImagePage[]): Uint8Array {
  const enc = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const push = (b: Uint8Array | string) => {
    const bytes = typeof b === 'string' ? enc.encode(b) : b;
    chunks.push(bytes);
    length += bytes.length;
  };
  // Objekt: 1 katalog, 2 sidträd, sedan per sida: sida, innehåll, bild
  const pageObj = (i: number) => 3 + i * 3;
  const begin = (n: number) => { offsets[n] = length; push(`${n} 0 obj\n`); };

  push('%PDF-1.4\n%\xE2\xE3\xCF\xD3\n');
  begin(1);
  push('<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');
  begin(2);
  push(`<< /Type /Pages /Count ${pages.length} /Kids [${pages.map((_, i) => `${pageObj(i)} 0 R`).join(' ')}] >>\nendobj\n`);
  pages.forEach((p, i) => {
    const w = (p.wMm * PT_PER_MM).toFixed(4);
    const h = (p.hMm * PT_PER_MM).toFixed(4);
    const n = pageObj(i);
    begin(n);
    push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w} ${h}] /Resources << /XObject << /Im${i} ${n + 2} 0 R >> >> /Contents ${n + 1} 0 R >>\nendobj\n`);
    const content = `q ${w} 0 0 ${h} 0 0 cm /Im${i} Do Q\n`;
    begin(n + 1);
    push(`<< /Length ${enc.encode(content).length} >>\nstream\n${content}endstream\nendobj\n`);
    begin(n + 2);
    push(`<< /Type /XObject /Subtype /Image /Width ${p.pxW} /Height ${p.pxH} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${p.jpeg.length} >>\nstream\n`);
    push(p.jpeg);
    push('\nendstream\nendobj\n');
  });
  const count = 3 + pages.length * 3;
  const xref = length;
  push(`xref\n0 ${count}\n0000000000 65535 f \n`);
  for (let n = 1; n < count; n++) push(`${String(offsets[n]).padStart(10, '0')} 00000 n \n`);
  push(`trailer\n<< /Size ${count} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

  const out = new Uint8Array(length);
  let pos = 0;
  for (const c of chunks) { out.set(c, pos); pos += c.length; }
  return out;
}
